import { Controller } from '@hotwired/stimulus';
import { csrfToken, localTime } from './helpers/general';
import { STATUS, statusFor, applyStatusClass } from './helpers/undistractor_status';
import UndistractorOptionStore from '../stores/undistractor_option_store';

export default class extends Controller {
  static targets = ['activationText', 'icon', 'checkbox', 'switch', 'toggleSchedule'];

  static values = {
    isUpdating: { type: Boolean, default: false },
    undistractorOption: Object,
    // UI-only toggles — no extension sync, server or analytics. For tours and demos.
    dummyClick: { type: Boolean, default: false },
    notSignedIn: Boolean,
    posthogEvents: Object,
    // Which surface hosts this row (settings_page/activated_page/domain_page) — sent with the
    // activation PATCH for the PostHog `source` property.
    source: { type: String, default: 'settings_page' },
  };

  static UNCOLOR_CSS_CLASS = 'uncolor';
  static SUBMIT_ACTIVE_STATE_URL = '/undistractor_settings/update_activation_state';

  connect() {
    this.#initializeTooltips();

    // Connect to the central store
    this.store = UndistractorOptionStore.getInstance();

    // Subscribe to changes for this specific undistractor option
    // The store will immediately call this with the current value if it exists
    this.unsubscribe = this.store.subscribe(this.undistractorOptionValue.id, (data) => {
      this.#syncFromStore(data);
    });
  }

  disconnect() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  // The switch is binary over the decoupled model: visually-on means
  // active OR scheduled. Turning off clears both (a scheduled window left active would keep
  // the extension blocking); turning on enters Always mode.
  toggleActivationState() {
    if (this.isUpdatingValue) return;

    // The change event beats the click's outside-click dismiss to the document, so close the editor
    // here rather than relying on that race — flipping the switch must never leave it floating open.
    this.#closeScheduleEditor();

    const { active } = this.undistractorOptionValue;
    // Both branches below hand off to the schedule controller, whose master-off deletes the window for real —
    // out of bounds for a UI-only toggle, so dummy mode never takes them.
    const schedule = this.dummyClickValue ? null : this.undistractorOptionValue.schedule;
    if (!active && schedule) {
      // Scheduled mode -> off: the schedule is what's running; remove it (no bounce to Always).
      this.element.querySelector('.undistractor-card-master-off')?.click();
      this.#updateCheckboxState();
      return;
    }
    if (active && schedule) {
      // Always-with-remembered-window -> off: clear both.
      this.element.querySelector('.undistractor-card-master-off')?.click();
    }
    this.#setActive(!active);
  }

  // Strip actions: the second decision (Always vs Scheduled) lives here, never in the switch.
  //
  // `force` is for the schedule controller's reconcile. The in-flight guard is there to drop a user
  // toggle that races a save, but a correction after a failed save is authoritative and must land
  // regardless — enterScheduledMode's PATCH is still open at exactly that moment, so without this the
  // revert is swallowed and a create that merely failed to persist leaves the tool switched Off.
  // Landing it mid-save is safe because #setActive queues the round trip behind the open one rather
  // than firing alongside it; `force` means "don't drop me", not "go now".
  activateAlways({ force = false } = {}) {
    if (force || !this.isUpdatingValue) this.#setActive(true);
  }

  resumeSchedule({ force = false } = {}) {
    const resume = () => {
      if (this.undistractorOptionValue.schedule) this.#setActive(false);
    };

    if (force || !this.isUpdatingValue) resume();
  }

  // Called by the schedule controller after a window is saved: choosing a schedule IS
  // choosing Scheduled mode, so Always (active) switches off.
  enterScheduledMode() {
    if (this.undistractorOptionValue.active) this.#setActive(false);
  }

  #setActive(newActiveStatus) {
    // Show 'updating' spinner and disable activation checkbox
    this.#setUpdatingState();
    this.undistractorOptionValue = { ...this.undistractorOptionValue, active: newActiveStatus };

    // Dispatches to the extension immediately on purpose — the block should apply as the switch
    // slides, and a navigation away must not be able to eat it. Only the server round trip below is
    // deferred. A failed save reverts and re-dispatches.
    this.store.upsert(this.undistractorOptionValue, this.dummyClickValue);

    if (this.dummyClickValue) {
      this.#unsetUpdatingState();
      return;
    }

    if (this.notSignedInValue) {
      this.#captureLitePosthogEvent(newActiveStatus);
      this.#unsetUpdatingState();
      return;
    }

    // One PATCH per row at a time: update_activation_state finds the setting by the column it is
    // about to write (find_or_initialize_by(active: true, ...)), so two in flight for one option can
    // interleave and lose one — and the server can't fix that alone, since it can't know which
    // request the client considers last. Only the schedule reconcile's forced correction can queue
    // behind another write (plain toggles are dropped by the isUpdating guard), so the chain never
    // holds more than one waiter and the queued write is always the authoritative one.
    this.pendingSync = (this.pendingSync ?? Promise.resolve())
      .catch(() => {}) // a failed link must not poison the chain for the write behind it
      .then(() => this.#remoteActiveStateSync(newActiveStatus, this.undistractorOptionValue.id))
      .catch(() => ({ ok: false })) // a rejected fetch must still unwind the saving state, not strand the row dimmed and dead
      .then((response) => {
        // Before anything else: the checkbox anchors the error tooltip, and #setUpdatingState left
        // it display:none. Popper measures an all-zero rect off a hidden anchor and pins the tip to
        // the viewport origin — and never re-measures once the anchor reappears.
        this.#unsetUpdatingState();
        if (!response.ok) {
          // Each write undoes itself: with writes queued, the store's current value may already be
          // a newer write's paint, so invert this write's own target rather than negating the store.
          this.undistractorOptionValue = { ...this.undistractorOptionValue, active: !newActiveStatus };
          this.store.upsert(this.undistractorOptionValue);
          this.dispatch('saveFailed');
          this.#showTooltip(this.tooltips.switch, this.switchTarget);
          this.#showSidebarSwitchTooltip();
        }
      });
  }

  undistractorOptionValueChanged() {
    this.#updateUndistractorRow();

    // Re-assert the checkbox after #updateUndistractorRow() (which already synced it once): when the
    // sidebar proxies a click to this checkbox, the browser's native toggle and the store-subscription
    // callback can race and leave it wrong. A second #updateCheckboxState() pins it to the Stimulus value.
    this.#updateCheckboxState();
  }

  // -----------------------------------------------------------
  // ------------ Private Methods --------------------------- //
  // -----------------------------------------------------------

  // The schedule editor is owned by the sibling `schedule` controller on this same element.
  #closeScheduleEditor() {
    this.application.getControllerForElementAndIdentifier(this.element, 'schedule')?.cancelScheduleForm();
  }

  #initializeTooltips() {
    const tooltipTriggerList = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');

    this.tooltips = {};
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      this.tooltips[tooltipTriggerEl.dataset.undistractorSettingsPageRowTarget] = new bootstrap.Tooltip(tooltipTriggerEl);
    });
  }

  // Two separate jobs: `d-none` removes the hit area so a competing click can't land mid-save,
  // and `is-saving` is the hook for the switch's dim. A save that resolves in the same tick
  // (dummy click, signed out) adds and removes the class with no style recalc between, so the
  // dim never starts; CSS delays it past the fast case for everything else.
  #setUpdatingState() {
    this.isUpdatingValue = true;
    this.checkboxTarget.classList.add('d-none');
    this.element.classList.add('is-saving');
  }

  #unsetUpdatingState() {
    this.isUpdatingValue = false;
    this.checkboxTarget.classList.remove('d-none');
    this.element.classList.remove('is-saving');

    // A mid-save row click flips the checkbox natively but is dropped by the
    // isUpdatingValue guard, so re-assert from the authoritative value.
    this.#updateCheckboxState();
  }

  #updateUndistractorRow() {
    this.#updateStatus();
    this.#updateCheckboxState();
    this.#updateToggleSchedule();
  }

  #updateToggleSchedule() {
    if (!this.hasToggleScheduleTarget) return;

    this.toggleScheduleTarget.textContent = this.undistractorOptionValue.schedule || '';
  }

  #updateStatus() {
    const status = statusFor(this.undistractorOptionValue);

    this.activationTextTarget.textContent = status;
    applyStatusClass(this.element, status);
    // Off rows grey their icon; both on states keep it in colour.
    this.iconTarget.classList.toggle(this.constructor.UNCOLOR_CSS_CLASS, status === STATUS.INACTIVE);
  }

  #updateCheckboxState() {
    const visuallyOn = this.undistractorOptionValue.active || !!this.undistractorOptionValue.schedule;
    if (visuallyOn) {
      this.checkboxTarget.setAttribute('checked', 'checked');
    } else {
      this.checkboxTarget.removeAttribute('checked');
    }
    this.checkboxTarget.checked = visuallyOn;
  }

  // Signed-out toggles never reach the server, so PostHog capture happens here. Event names arrive
  // from Ruby (PosthogEvents) via the posthog-events data value — lite-only, so signed-in pages skip this.
  #captureLitePosthogEvent(isActive) {
    const events = this.posthogEventsValue;
    const name = isActive ? events.activated : events.deactivated;
    if (name) window.posthog?.capture(name, { ...events.properties, undistractor_option_id: this.undistractorOptionValue.id });
  }

  #syncFromStore(data) {
    // Update local values from the store data
    // This will trigger the *ValueChanged callbacks to update the UI
    this.undistractorOptionValue = { ...this.undistractorOptionValue, ...data };
  }

  // Popper measures the anchor once, at show() time, and never re-measures. An anchor with no box
  // yields an all-zero rect and pins the tip to the viewport origin — which happens two ways here:
  // the d-none hit layer during a save, and a row the tab filter has hidden (its wrapper carries
  // d-none, so the checkbox is unrendered even though the row exists). Whichever surface the click
  // came from is by definition visible, so skipping the other one loses nothing.
  #showTooltip(tooltip, anchor) {
    if (tooltip && anchor?.getClientRects().length) tooltip.show();
  }

  // The sidebar item is cloned from a template, so its tooltip is instantiated lazily here rather
  // than by #initializeTooltips (which only walks this row). Absent for a tool that has never been
  // active — the sidebar only grows an item once one appears in the store.
  #showSidebarSwitchTooltip() {
    const sidebarSwitch = this.#getSidebarSwitch();
    if (!sidebarSwitch) return;

    this.#showTooltip(bootstrap.Tooltip.getOrCreateInstance(sidebarSwitch), sidebarSwitch);
  }

  #getSidebarRow() {
    return document.querySelector(`.active-sidebar-undistractor-row[data-undistractor-option-id="${this.undistractorOptionValue.id}"]`);
  }

  #getSidebarSwitch() {
    return this.#getSidebarRow()?.querySelector('.undistractor-sidebar-switch');
  }

  async #remoteActiveStateSync(isActive, undistractorOptionId) {
    const response = await fetch(this.constructor.SUBMIT_ACTIVE_STATE_URL, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        undistractor_settings: { active: isActive, undistractor_option_id: undistractorOptionId, local_time: localTime(), source: this.sourceValue },
      }),
    });

    return response;
  }
}
